import type { Pill } from "@ui/Pill";
import type { ComponentProps } from "solid-js";

export type ArtifactKindPillTheme = NonNullable<
	ComponentProps<typeof Pill>["theme"]
>;

export const KIND_CONFIG: Record<
	string,
	{
		label: string;
		symbol: string;
		smallSymbol: string;
		pillTheme: ArtifactKindPillTheme;
		/**
		 * The colour the kind is known by, wherever it is marked: the answers
		 * list fills its tile with it, the question box's Answers tab draws its
		 * symbol in it over a wash of it.
		 */
		colour: string;
	}
> = {
	report: {
		label: "Document",
		symbol: "description",
		smallSymbol: "description",
		pillTheme: "document",
		colour: "var(--colour-document)",
	},
	dataset: {
		label: "Dataset",
		symbol: "table_chart",
		smallSymbol: "table_chart",
		pillTheme: "success",
		colour: "var(--colour-dataset)",
	},
	clip: {
		label: "Clip",
		symbol: "video_library",
		smallSymbol: "smart_display",
		pillTheme: "primary",
		colour: "var(--colour-video)",
	},
	board: {
		label: "Board",
		symbol: "space_dashboard",
		smallSymbol: "space_dashboard",
		pillTheme: "primary",
		colour: "var(--colour-video)",
	},
	export: {
		label: "Table",
		symbol: "download",
		smallSymbol: "download",
		pillTheme: "warning",
		colour: "var(--colour-export)",
	},
	other: {
		label: "Other",
		symbol: "category",
		smallSymbol: "category",
		pillTheme: "muted",
		colour: "var(--colour-other)",
	},
	csv: {
		label: "CSV",
		symbol: "table_chart",
		smallSymbol: "table_chart",
		pillTheme: "success",
		colour: "var(--colour-csv)",
	},
	deck: {
		label: "Presentation",
		symbol: "burst_mode",
		smallSymbol: "burst_mode",
		pillTheme: "other",
		colour: "var(--colour-presentation)",
	},
	answer: {
		label: "Answer",
		symbol: "chat",
		smallSymbol: "chat",
		pillTheme: "muted",
		colour: "var(--grey-600)",
	},
	not_processable: {
		label: "Not processable",
		symbol: "warning",
		smallSymbol: "warning",
		pillTheme: "warning",
		colour: "var(--colour-fg-3)",
	},
};

/** Stream `answer_type` values and legacy card keys → {@link KIND_CONFIG} keys. */
export const CHAT_SPEAKING_TYPE_TO_KIND: Record<string, string> = {
	answer: "answer",
	asset_pptx: "deck",
	clips: "clip",
	csv: "csv",
	document: "report",
	not_processable: "not_processable",
};

/** Inline `artifact_created` types → {@link KIND_CONFIG} keys. */
export const CHAT_ARTIFACT_TYPE_TO_KIND: Record<string, string> = {
	video_sources: "clip",
	deck: "deck",
	csv: "csv",
	document: "report",
	board: "board",
};

export function kindConfigForKey(kind: string) {
	return KIND_CONFIG[kind] ?? KIND_CONFIG.other;
}

export const getFileExtension = (path: string) => {
	const extension = path.split(".").pop();
	return extension ? extension.toLowerCase() : null;
};
